import React, { useCallback } from 'react';
import { Survey, Model } from "survey-react-ui";
import { Serializer } from "survey-core";
import "survey-core/survey-core.min.css";
import useIpAddress from "../../services/useIpAddress";
import themeJson from "./surveyTheme";

// Register custom properties for dropdown lazy loading
Serializer.addProperty("dropdown", {
    name: "customApiUrl:string",
    category: "general",
    visibleIndex: 10
});
Serializer.addProperty("dropdown", {
    name: "customApiResponsePath:string",
    category: "general",
    default: "results"
});
Serializer.addProperty("dropdown", {
    name: "customApiValueField:string",
    category: "general",
    default: "value"
});
Serializer.addProperty("dropdown", {
    name: "customApiTitleField:string",
    category: "general",
    default: "text"
});
Serializer.addProperty("dropdown", {
    name: "customMinSearchLength:number",
    category: "general",
    default: 3
});
/**
 * SurveyForm - a wrapper for survey.js (survey-react-ui)
 *
 * Props:
 *   json: Survey JSON schema
 *   onSubmit: function(surveyResult) => void
 *   initialData: (optional) prefilled data
 */
const SurveyForm = ({ json, onSubmit, initialData }) => {
    // Apply custom SurveyJS theme on mount
    React.useEffect(() => {
        // Apply theme CSS variables to document root
        if (themeJson && themeJson.cssVariables) {
            Object.entries(themeJson.cssVariables).forEach(([key, value]) => {
                document.documentElement.style.setProperty(key, value);
            });
        }
    }, []);

    const [ip] = useIpAddress();

    // Replace {ip_address} in the JSON (deep clone if needed)
    // Utility to get today's date in YYYY-MM-DD format
    function getToday() {
        const d = new Date();
        return d.toISOString().split('T')[0];
    }

    const processedJson = React.useMemo(() => {
        const jsonCopy = JSON.parse(JSON.stringify(json));        
        // Convert sections format to pages format if needed
        if (jsonCopy.sections && !jsonCopy.pages) {
            jsonCopy.pages = [];
            
            Object.entries(jsonCopy.sections).forEach(([sectionKey, section]) => {
                const page = {
                    name: sectionKey,
                    title: section.title || sectionKey,
                    description: section.description || '',
                    elements: []
                };
                
                if (section.fields) {
                    Object.entries(section.fields).forEach(([fieldKey, field]) => {
                        // Map field types to SurveyJS types
                        let surveyType = field.type;
                        if (field.type === 'date') {
                            surveyType = 'text';
                        } else if (field.type === 'checkbox') {
                            surveyType = 'boolean';
                        } else if (field.type === 'select') {
                            surveyType = 'dropdown';
                        } else if (field.type === 'radio') {
                            surveyType = 'radiogroup';
                        }
                        
                        const element = {
                            name: fieldKey,
                            type: surveyType,
                            title: field.label || fieldKey,
                            isRequired: field.required || false,
                            description: field.description || '',
                            placeholder: field.placeholder || '',
                            visible: field.type !== 'hidden',
                            readOnly: field.readonly || false
                        };
                        
                        // Handle choices for select/radio fields
                        if (field.options && Array.isArray(field.options)) {
                            element.choices = field.options.map(option => {
                                if (typeof option === 'string') {
                                    return { value: option, text: option };
                                }
                                return { value: option.value || option.key, text: option.label || option.text || option.value };
                            });
                        }
                        
                        // Set default values
                        if (field.value === 'current_date' || fieldKey === 'signatureDate') {
                            element.defaultValue = getToday();
                        } else if (fieldKey === 'ipAddress') {
                            element.defaultValue = ip || "";
                        } else if (field.defaultValue) {
                            element.defaultValue = field.defaultValue;
                        }
                        
                        // Handle input type for date fields
                        if (field.type === 'date') {
                            element.inputType = 'date';
                        }
                        
                        page.elements.push(element);
                    });
                }
                
                jsonCopy.pages.push(page);
            });
            
            // Remove the sections property since we've converted it to pages
            delete jsonCopy.sections;
        }
        
        // Process existing pages structure (original logic)
        if (jsonCopy.pages && Array.isArray(jsonCopy.pages)) {
            jsonCopy.pages.forEach(page => {
                page.elements?.forEach(el => {
                    if (el.name === "ipAddress" && el.defaultValue === "{ip_address}") {
                        el.defaultValue = ip || "";
                    }
                    if (el.name === "signatureDate" && el.defaultValue === "{current_date}") {
                        el.defaultValue = getToday();
                    }
                });
            });
        }
        
        return jsonCopy;
    }, [ip, json]);
    // Optionally prefill data
    const surveyModel = React.useMemo(() => {
        const model = new Model(processedJson);
        if (initialData) {
            model.data = initialData;
        }
        return model;
    }, [processedJson, initialData]);

    // Add autocomplete/lazy load handler for dropdowns
    React.useEffect(() => {
        let searchTimeout;
        let abortController = null;

        const handleChoicesLazyLoad = async (_sender, options) => {
            const searchText = options.filter || "";
            const question = options.question;

            // Get custom properties from the question JSON
            const apiUrl = question.customApiUrl;
            const minSearchLength = question.customMinSearchLength || 3;

            // Only trigger search if user typed minimum characters
            if (searchText.length < minSearchLength) {
                options.setItems([], 0);
                return;
            }

            // If no API URL is configured, skip
            if (!apiUrl) {
                return;
            }

            // Cancel previous request if still pending
            if (abortController) {
                abortController.abort();
            }

            // Clear previous timeout (debouncing)
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }

            // Create new AbortController for this request
            abortController = new AbortController();
            const currentAbortController = abortController;

            // Debounce: wait 300ms after user stops typing
            searchTimeout = setTimeout(async () => {
                try {
                    // Build query string for CMS.gov API
                    const params = new URLSearchParams();
                    params.append('version', 2.1);
                    params.append('organization_name', searchText+'*');
                    params.append('offset', options.skip || 0);
                    params.append('limit', options.take || 20);

                    const url = `${apiUrl}?${params.toString()}`;

                    const response = await fetch(url, {
                        signal: currentAbortController.signal
                    });

                    if (!response.ok) {
                        throw new Error('API request failed');
                    }

                    const data = await response.json();

                    // Get custom property values
                    const resultsPath = question.customApiResponsePath || 'results';
                    const valueName = question.customApiValueField || 'value';
                    const titleName = question.customApiTitleField || 'text';

                    // Extract results from nested path
                    const results = resultsPath.split('.').reduce((obj, key) => obj?.[key], data) || [];

                    // Transform API results to SurveyJS choice format
                    const choices = results.map(item => {
                        // Support nested properties like "basic.organization_name"
                        const getValue = (obj, path) => path.split('.').reduce((o, k) => o?.[k], obj);

                        const choice = {
                            value: getValue(item, valueName),
                            text: getValue(item, titleName)
                        };

                        // Store full API response data as a custom property
                        // This will be accessible via choice.customData
                        choice['customData'] = item;

                        return choice;
                    });

                    // Filter out any null/undefined values
                    const validChoices = choices.filter(c => c.value && c.text);

                    // Set the choices - SurveyJS will convert these to ItemValue objects
                    options.setItems(validChoices, validChoices.length);

                    // After items are set, attach customData to the ItemValue objects
                    // We need to do this because SurveyJS converts plain objects to ItemValue instances
                    validChoices.forEach((choice, index) => {
                        const itemValue = question.choices[index];
                        if (itemValue && choice.customData) {
                            // Set customData on the ItemValue object
                            itemValue.customData = choice.customData;
                        }
                    });

                } catch (error) {
                    // Don't log error if request was aborted (expected behavior)
                    if (error.name !== 'AbortError') {
                        options.setItems([], 0);
                    }
                }
            }, 300); // 300ms debounce delay
        };

        surveyModel.onChoicesLazyLoad.add(handleChoicesLazyLoad);

        return () => {
            surveyModel.onChoicesLazyLoad.remove(handleChoicesLazyLoad);
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
            if (abortController) {
                abortController.abort();
            }
        };
    }, [surveyModel]);

    // Handle value changes to store full organization data
    React.useEffect(() => {
        const handleValueChanged = (sender, options) => {
            if (options.name && options.value !== undefined) {
                const question = sender.getQuestionByName(options.name);

                // Only process dropdowns with lazy loading enabled
                if (question && question.getType() === 'dropdown' && question.choicesLazyLoadEnabled) {
                    const selectedChoice = question.choices.find(c => c.value === options.value);

                    if (selectedChoice && selectedChoice.customData) {
                        sender.setVariable(options.name + "_data", selectedChoice.customData);

                        // Dispatch custom event for external listeners
                        window.dispatchEvent(new CustomEvent('surveyValueChanged', {
                            detail: {
                                questionName: options.name,
                                value: options.value,
                                customData: selectedChoice.customData
                            }
                        }));
                    }
                }
            }
        };

        surveyModel.onValueChanged.add(handleValueChanged);

        return () => {
            surveyModel.onValueChanged.remove(handleValueChanged);
        };
    }, [surveyModel]);

    // Handle survey completion
    const handleComplete = useCallback((sender) => {
        if (onSubmit){
            onSubmit(sender.data);
        }
    }, [onSubmit]);

    React.useEffect(() => {
        surveyModel.onComplete.add(handleComplete);
        return () => surveyModel.onComplete.remove(handleComplete);
    }, [surveyModel, handleComplete]);

    return (
        <div style={{ margin: '0 auto' }}>
            <Survey model={surveyModel} />
        </div>
    );
};

export default SurveyForm;
